import sessionApi from './apiClient';
import { ErrorType } from '../types/errors';
import { ErrorHandler } from '../utils/errorHandler';
import { DepartmentSummary } from '../types/domain';

export interface DepartmentPayload {
  name: string;
  code: string;
  type: 'station' | 'unit' | string;
  parent?: number | null;
  description?: string;
  is_active?: boolean;
}

export interface DepartmentListParams {
  includeInactive?: boolean;
}

export const userService = {
  getUsers: async (page = 1) => {
    return ErrorHandler.retryRequest(async () => {
      try {
        const response = await sessionApi.get(`/users/?page=${page}`);
        return response.data;
      } catch (error) {
        throw ErrorHandler.classifyError(error);
      }
    }, 3);
  },

  createUser: async (userData: any) => {
    try {
      const response = await sessionApi.post('/users/', userData);
      return response.data;
    } catch (error) {
      const apiError = ErrorHandler.classifyError(error);
      if (apiError.type === ErrorType.VALIDATION) {
        apiError.validationErrors = ErrorHandler.extractValidationErrors(error);
      }
      throw apiError;
    }
  },

  updateUser: async (userId: number, userData: any) => {
    try {
      const response = await sessionApi.patch(`/users/${userId}/`, userData);
      return response.data;
    } catch (error) {
      const apiError = ErrorHandler.classifyError(error);
      if (apiError.type === ErrorType.VALIDATION) {
        apiError.validationErrors = ErrorHandler.extractValidationErrors(error);
      }
      throw apiError;
    }
  },

  deactivateUser: async (userId: number) => {
    try {
      const response = await sessionApi.post(`/users/${userId}/deactivate/`);
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  activateUser: async (userId: number) => {
    try {
      const response = await sessionApi.post(`/users/${userId}/activate/`);
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  unlockUser: async (userId: number) => {
    try {
      const response = await sessionApi.post(`/users/${userId}/unlock/`);
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  getUserById: async (userId: number) => {
    return ErrorHandler.retryRequest(async () => {
      try {
        const response = await sessionApi.get(`/users/${userId}/`);
        return response.data;
      } catch (error) {
        throw ErrorHandler.classifyError(error);
      }
    }, 2);
  },

  getStats: async () => {
    try {
      const response = await sessionApi.get('/users/stats/');
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  getRoleStats: async () => {
    try {
      const response = await sessionApi.get('/roles/stats/');
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  getDepartments: async (params: DepartmentListParams = {}): Promise<DepartmentSummary[]> => {
    const response = await sessionApi.get('/departments/', {
      params: params.includeInactive ? { include_inactive: 'true' } : undefined,
    });
    return Array.isArray(response.data) ? response.data : response.data?.results || [];
  },

  createDepartment: async (payload: DepartmentPayload): Promise<DepartmentSummary> => {
    try {
      const response = await sessionApi.post('/departments/', payload);
      return response.data;
    } catch (error) {
      const apiError = ErrorHandler.classifyError(error);
      if (apiError.type === ErrorType.VALIDATION) {
        apiError.validationErrors = ErrorHandler.extractValidationErrors(error);
      }
      throw apiError;
    }
  },

  updateDepartment: async (id: number, payload: Partial<DepartmentPayload>): Promise<DepartmentSummary> => {
    try {
      const response = await sessionApi.patch(`/departments/${id}/`, payload);
      return response.data;
    } catch (error) {
      const apiError = ErrorHandler.classifyError(error);
      if (apiError.type === ErrorType.VALIDATION) {
        apiError.validationErrors = ErrorHandler.extractValidationErrors(error);
      }
      throw apiError;
    }
  },

  activateDepartment: async (id: number): Promise<DepartmentSummary> => {
    const response = await sessionApi.post(`/departments/${id}/activate/`);
    return response.data;
  },

  deactivateDepartment: async (id: number): Promise<DepartmentSummary> => {
    const response = await sessionApi.post(`/departments/${id}/deactivate/`);
    return response.data;
  },

  assignDepartment: async (userId: number, departmentId: number) => {
    try {
      const response = await sessionApi.post(`/users/${userId}/assign-department/`, {
        department: departmentId,
      });
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  unassignDepartment: async (userId: number) => {
    try {
      const response = await sessionApi.post(`/users/${userId}/unassign-department/`);
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  assignFirearmDepartment: async (firearmId: number, payload: { department: number; reason: string }) => {
    const response = await sessionApi.post(`/admin/system-governance/firearms/${firearmId}/assign-department/`, payload);
    return response.data;
  },

  assignOfficerDepartment: async (officerId: number, payload: { department: number; reason: string }) => {
    const response = await sessionApi.post(`/admin/system-governance/officers/${officerId}/assign-department/`, payload);
    return response.data;
  },
};

export interface QaUserPayload {
  username: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active?: boolean;
}

export type AuditExportFormat = 'csv' | 'pdf';

export interface AuditExportResult {
  blob: Blob;
  contentDisposition?: string;
}

export interface SecurityLogDetails {
  username?: string | null;
  user_id?: number | string | null;
  reason?: string | null;
  failed_attempt_count?: number | null;
  locked_at?: string | null;
  lockout_until?: string | null;
  lockout_duration_minutes?: number | null;
  unlock_type?: string | null;
  unlocked_by_username?: string | null;
  unlocked_by_id?: number | string | null;
  result?: string | null;
  status?: string | null;
  severity?: string | null;
  [key: string]: unknown;
}

export interface SecurityLogEntry {
  id: number;
  timestamp: string;
  user?: number | null;
  username?: string | null;
  source_ip?: string | null;
  event: string;
  details?: SecurityLogDetails | null;
}

export interface SecurityLogFilters {
  event?: string;
  event_type?: 'lockout';
}

export interface SecurityLogPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: SecurityLogEntry[];
}

export interface SiemSummary {
  events: number;
  failed_logins: number;
  failed_mfa_audit: number;
  window_start: string;
  window_end: string;
  alerts: number;
  pending_outbox: number;
  sources: Array<{ source: string; last_received_at: string | null; last_gap_at: string | null;
    gap_note: string; last_scanned_at: string | null; scan_interval_seconds: number | null;
    scan_overdue: boolean }>;
}

export interface SiemEvent {
  id: number;
  source: string;
  category: string;
  occurred_at: string;
  metadata?: Record<string, unknown>;
}

export interface SiemEventFilters {
  source?: string;
  category?: string;
  from?: string;
  before?: string;
}

export interface SiemPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: SiemEvent[];
}

export const securityService = {
  getSiemSummary: async (): Promise<SiemSummary> => {
    const response = await sessionApi.get('/siem/events/summary/');
    return response.data;
  },

  getSiemEvents: async (page = 1, filters: SiemEventFilters = {}): Promise<SiemPage> => {
    const response = await sessionApi.get('/siem/events/', { params: { page, ...filters } });
    return response.data;
  },
  getAuditLogs: async (filters: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    const query = params.toString();
    const response = await sessionApi.get(query ? `/audit-logs/?${query}` : '/audit-logs/');
    return Array.isArray(response.data) ? response.data : response.data?.results || [];
  },

  exportAuditLogs: async (
    filters: Record<string, string>,
    format: AuditExportFormat
  ): Promise<AuditExportResult> => {
    const response = await sessionApi.get('/audit-logs/export/', {
      params: {
        ...filters,
        format,
      },
      responseType: 'blob',
    });
    const contentDisposition = response.headers['content-disposition'];
    return {
      blob: response.data,
      contentDisposition: typeof contentDisposition === 'string' ? contentDisposition : undefined,
    };
  },

  getSecurityLogs: async (filters: SecurityLogFilters = {}): Promise<SecurityLogEntry[]> => {
    const response = await sessionApi.get('/security-logs/', { params: filters });
    return Array.isArray(response.data) ? response.data : response.data?.results || [];
  },

  getSecurityLogPage: async (filters: SecurityLogFilters, page = 1): Promise<SecurityLogPage> => {
    const response = await sessionApi.get('/security-logs/', { params: { ...filters, page } });
    return response.data;
  },

  verifyAuditChain: async () => {
    const response = await sessionApi.get('/audit-logs/verify-chain/');
    return response.data;
  },

  getBackupStatus: async () => {
    const response = await sessionApi.get('/security/backups/status/');
    return response.data;
  },

  runBackup: async () => {
    const response = await sessionApi.post('/security/backups/run/', { backup_type: 'database' });
    return response.data;
  },

  getBackupRuns: async () => {
    const response = await sessionApi.get('/security/backups/');
    return Array.isArray(response.data) ? response.data : response.data?.results || [];
  },
};
